import React, { useEffect, useState } from 'react';
import { 
  obterMonografia,
  resubmeterMonografiaAposCorrecoes,
  submeterParaDirecaoCientifica,
  getStatusDisplayName,
  getStatusColor,
  getAvailableActions,
  // NOVOS IMPORTS PARA HISTÓRICO:
  obterHistoricoMonografia,
  obterEstatisticasVersionamento,
  obterVersaesDocumentos
} from '../../services/monografiaService';
import { useParams, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Paper,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Input,
  Grid
} from '@mui/material';
import {
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  RemoveRedEye as EyeIcon,
  Upload as UploadIcon,
  Send as SendIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Importando o componente de relatório de similaridade
import RelatorioSimilaridadeMonografia from './RelatorioSimilaridadeMonografia';

const MonografiaDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const userType = authUser?.tipo || '';

  const [monografia, setMonografia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // NOVOS ESTADOS PARA HISTÓRICO
  const [historicoDialogOpen, setHistoricoDialogOpen] = useState(false);
  const [versaoDialogOpen, setVersaoDialogOpen] = useState(false);
  const [historicoData, setHistoricoData] = useState(null);
  const [versaoData, setVersaoData] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };
  const [actionLoading, setActionLoading] = useState(false);
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleResubmeterMonografia = async () => {
    // Abrir diálogo para upload de arquivo
    setUploadDialogOpen(true);
  };

  const handleConfirmResubmissao = async () => {
    try {
      setActionLoading(true);
      setUploadDialogOpen(false);
      
      if (selectedFile) {
        showNotification('📤 Resubmetendo monografia com nova versão...', 'info');
        await resubmeterMonografiaAposCorrecoes(monografia.id, selectedFile);
        showNotification('✅ Nova versão da monografia submetida com sucesso!', 'success');
      } else {
        showNotification('🔄 Resubmetendo monografia sem alterações no arquivo...', 'info');
        await resubmeterMonografiaAposCorrecoes(monografia.id);
        showNotification('✅ Monografia resubmetida com sucesso!', 'success');
      }
      
      // Recarregar dados da monografia
      const response = await obterMonografia(id);
      setMonografia(response.data);
      setSelectedFile(null);
    } catch (error) {
      console.error('Erro ao resubmeter:', error);
      showNotification('❌ Erro ao resubmeter monografia. Tente novamente.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      const allowedTypes = ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
      if (!allowedTypes.includes(file.type)) {
        showNotification('❌ Apenas arquivos Word (.doc, .docx) são permitidos', 'error');
        return;
      }
      
      // Validar tamanho (10MB)
      if (file.size > 10 * 1024 * 1024) {
        showNotification('❌ Arquivo muito grande. Tamanho máximo: 10MB', 'error');
        return;
      }
      
      setSelectedFile(file);
      showNotification('✅ Arquivo selecionado com sucesso', 'success');
    }
  };

  const handleSubmeterParaDirecao = async () => {
    try {
      setActionLoading(true);
      showNotification('🏛️ Submetendo à Direção Científica...', 'info');
      
      await submeterParaDirecaoCientifica(monografia.id);
      
      showNotification('✅ Monografia submetida à Direção Científica!', 'success');
      
      // Recarregar dados da monografia
      const response = await obterMonografia(id);
      setMonografia(response.data);
    } catch (error) {
      console.error('Erro ao submeter à direção:', error);
      showNotification('❌ Erro ao submeter à direção. Tente novamente.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // NOVAS FUNÇÕES PARA HISTÓRICO
  const handleAbrirHistorico = async () => {
    try {
      setLoadingHistorico(true);
      showNotification('🔍 Carregando histórico completo...', 'info');
      
      const response = await obterHistoricoMonografia(monografia.id);
      setHistoricoData(response.data);
      setHistoricoDialogOpen(true);
      
      showNotification('✅ Histórico carregado com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      showNotification('❌ Erro ao carregar histórico. Tente novamente.', 'error');
    } finally {
      setLoadingHistorico(false);
    }
  };

  const handleAbrirVersoes = async () => {
    try {
      setLoadingHistorico(true);
      showNotification('📋 Carregando versões do documento...', 'info');
      
      const response = await obterVersaesDocumentos(monografia.id);
      setVersaoData(response.data);
      setVersaoDialogOpen(true);
      
      showNotification('✅ Versões carregadas!', 'success');
    } catch (error) {
      console.error('Erro ao carregar versões:', error);
      showNotification('❌ Erro ao carregar versões. Tente novamente.', 'error');
    } finally {
      setLoadingHistorico(false);
    }
  };

  useEffect(() => {
    const fetchMonografia = async () => {
      try {
        setLoading(true);
        const response = await obterMonografia(id);
        setMonografia(response.data);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar monografia:', err);
        setError('Não foi possível carregar os detalhes da monografia.');
      } finally {
        setLoading(false);
      }
    };

    fetchMonografia();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!monografia) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Monografia não encontrada.
      </Alert>
    );
  }

  // Calcular status e cor usando as novas funções
  const statusColor = getStatusColor(monografia.status);
  const statusDisplayName = getStatusDisplayName(monografia.status);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Detalhes da Monografia
      </Typography>

      <Card>
        <CardContent>
          {/* Cabeçalho: Versão e Status */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Versão: {monografia.versao_documento}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label={statusDisplayName}
                color={statusColor}
                sx={{ fontWeight: 'medium' }}
              />
              {monografia.numero_resubmissoes > 0 && (
                <Chip
                  label={`${monografia.numero_resubmissoes} resubmissão${monografia.numero_resubmissoes > 1 ? 'ões' : ''}`}
                  size="small"
                  variant="outlined"
                  color="info"
                />
              )}
              {/* NOVA INFORMAÇÃO: Versão atual */}
              <Chip
                label={`Versão atual: ${monografia.versao_atual || 'v1.0'}`}
                size="small"
                variant="outlined"
                color="primary"
              />
            </Box>
          </Box>

          {/* Informações Básicas */}
          <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon color="action" />
              <Typography variant="body1">
                <strong>Data de Submissão:</strong>{' '}
                {monografia.data_submissao ? new Date(monografia.data_submissao).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
            
            {monografia.data_resubmissao && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RefreshIcon color="action" />
                <Typography variant="body1">
                  <strong>Data de Resubmissão:</strong>{' '}
                  {new Date(monografia.data_resubmissao).toLocaleDateString()}
                </Typography>
              </Box>
            )}

            {monografia.data_aprovacao_supervisor && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  <strong>Aprovada pelo Supervisor em:</strong>{' '}
                  {new Date(monografia.data_aprovacao_supervisor).toLocaleDateString()}
                </Typography>
              </Box>
            )}

            {monografia.data_submissao_direcao && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  <strong>Submetida à Direção em:</strong>{' '}
                  {new Date(monografia.data_submissao_direcao).toLocaleDateString()}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">
                <strong>ID do Projeto:</strong> {monografia.projeto_id}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Observações específicas baseadas no status */}
          {monografia.observacoes_supervisor && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="warning">
                <Typography variant="subtitle2">
                  <strong>📝 Correções Solicitadas pelo Supervisor:</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {monografia.observacoes_supervisor}
                </Typography>
                {monografia.data_solicitacao_correcoes && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.8 }}>
                    Solicitado em: {new Date(monografia.data_solicitacao_correcoes).toLocaleDateString()}
                  </Typography>
                )}
              </Alert>
            </Box>
          )}

          {monografia.observacoes_aprovacao_supervisor && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="success">
                <Typography variant="subtitle2">
                  <strong>Observações da Aprovação pelo Supervisor:</strong>
                </Typography>
                <Typography variant="body2">
                  {monografia.observacoes_aprovacao_supervisor}
                </Typography>
              </Alert>
            </Box>
          )}

          {monografia.observacoes_devolucao_direcao && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="error">
                <Typography variant="subtitle2">
                  <strong>Devolvida pela Direção Científica:</strong>
                </Typography>
                <Typography variant="body2">
                  {monografia.observacoes_devolucao_direcao}
                </Typography>
                {monografia.tipo_devolucao_direcao && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    <strong>Motivo:</strong> {monografia.tipo_devolucao_direcao === 'FORMAL' ? 'Problemas Formais' : 'Problemas de Conteúdo'}
                  </Typography>
                )}
              </Alert>
            </Box>
          )}

          {monografia.prazo_correcoes && monografia.status === 'CORRECOES_SOLICITADAS' && (
            <Box sx={{ mb: 3 }}>
              <Alert 
                severity={new Date(monografia.prazo_correcoes) < new Date() ? 'error' : 'warning'}
              >
                <Typography variant="subtitle2">
                  <strong>Prazo para Correções:</strong>
                </Typography>
                <Typography variant="body2">
                  {new Date(monografia.prazo_correcoes).toLocaleDateString()}
                  {new Date(monografia.prazo_correcoes) < new Date() && ' (VENCIDO)'}
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Documento da Monografia */}
          {monografia.documento_monografia && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Documento da Monografia
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'background.default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography variant="body1">
                  {monografia.documento_monografia}
                </Typography>
                <Button
  variant="contained"
  color="primary"
  startIcon={<EyeIcon />}
  href={`${window.location.hostname === 'localhost' 
    ? 'http://localhost:5000' 
    : 'https://api.zenkroll.co.mz'}/api/documentos/view/${monografia.documento_monografia}`}
  target="_blank"
>
  Visualizar
</Button>

              </Paper>
            </Box>
          )}

          {/* Observações gerais se houver */}
          {monografia.observacoes && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Observações Gerais
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="body1">
                  {monografia.observacoes}
                </Typography>
              </Paper>
            </Box>
          )}

      {monografia && userType === 'chefe_departamento' && (
  <RelatorioSimilaridadeMonografia monografiaId={monografia.id} monografia={monografia} />
)}

          {/* Botões de Ação */}
          <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
     
            {/* Botões condicionais baseados no status e tipo de usuário */}
            {userType === 'estudante' && monografia && (() => {
              const availableActions = getAvailableActions('estudante', monografia.status);
              
              return availableActions.map((action) => {
                if (action.key === 'resubmeter') {
                  return (
                    <Button
                      key={action.key}
                      variant="contained"
                      color="warning"
                      startIcon={actionLoading ? <CircularProgress size={16} /> : <UploadIcon />}
                      onClick={handleResubmeterMonografia}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Resubmetendo...' : 'Resubmeter após Correções'}
                    </Button>
                  );
                }
                
                if (action.key === 'submeter-direcao') {
                  return (
                    <Button
                      key={action.key}
                      variant="contained"
                      color="primary"
                      startIcon={actionLoading ? <CircularProgress size={16} /> : <SendIcon />}
                      onClick={handleSubmeterParaDirecao}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Submetendo...' : 'Submeter à Direção Científica'}
                    </Button>
                  );
                }
                
                return null;
              });
            })()}

            {/* Exibir o botão de Agendar Defesa se for Chefe e monografia estiver APROVADA */}
            {(monografia.status === 'APROVADA' || 
  monografia.status === 'APROVADA_PELA_DIRECCAO_CIENTIFICA') && 
  userType === 'chefe_departamento' && (
  <Button
    variant="contained"
    color="primary"
    startIcon={<EventIcon />}
    onClick={() => navigate(`/defesas/agendar?monografiaId=${monografia.id}`)}
  >
    Agendar Defesa
  </Button>
)}

            {/* Visualizar Documento sempre disponível */}
<Button
  variant="contained"
  color="info"
  startIcon={<DescriptionIcon />}
  href={`${window.location.hostname === 'localhost' 
    ? 'http://localhost:5000' 
    : 'https://api.zenkroll.co.mz'}/api/documentos/view/${monografia.documento_monografia}`}
  target="_blank"
>
  Visualizar Documento
</Button>




            {/* NOVOS BOTÕES DE HISTÓRICO */}
            <Button
              variant="outlined"
              color="info"
              startIcon={<EventIcon />}
              onClick={handleAbrirHistorico}
              disabled={loadingHistorico}
            >
              {loadingHistorico ? 'Carregando...' : 'Ver Histórico'}
            </Button>

            {monografia.numero_resubmissoes > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DescriptionIcon />}
                onClick={handleAbrirVersoes}
                disabled={loadingHistorico}
              >
                Ver Versões
              </Button>
            )}

            {/* Voltar */}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Voltar
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Diálogo de Upload para Resubmissão */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          📤 Resubmeter Monografia Corrigida
        </DialogTitle>
        <DialogContent>
          {monografia.observacoes_supervisor && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2">
                <strong>Correções Solicitadas:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {monografia.observacoes_supervisor}
              </Typography>
            </Alert>
          )}
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Selecione o arquivo corrigido da sua monografia ou prossiga sem alterações no documento:
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <input
              accept=".doc,.docx"
              style={{ display: 'none' }}
              id="upload-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="upload-file">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ mb: 1 }}
              >
                {selectedFile ? 'Trocar Arquivo' : 'Selecionar Nova Versão (Opcional)'}
              </Button>
            </label>
            
            {selectedFile && (
              <Alert severity="success" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Arquivo selecionado:</strong> {selectedFile.name}
                  <br />
                  <strong>Tamanho:</strong> {(selectedFile.size / (1024 * 1024)).toFixed(2)}MB
                </Typography>
              </Alert>
            )}
          </Box>
          
          <Alert severity="info">
            <Typography variant="body2">
              • Se você selecionar um novo arquivo, a versão anterior será substituída<br />
              • Se não selecionar arquivo, apenas o status será atualizado<br />
              • Formatos aceitos: .doc, .docx (máximo 10MB)
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmResubmissao}
            variant="contained"
            color="warning"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {actionLoading ? 'Resubmetendo...' : 'Resubmeter Monografia'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* NOVOS DIÁLOGOS DE HISTÓRICO E VERSÕES */}
      {/* ============================================ */}

      {/* DIÁLOGO DE HISTÓRICO COMPLETO */}
      <Dialog
        open={historicoDialogOpen}
        onClose={() => setHistoricoDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          📋 Histórico Completo da Monografia
        </DialogTitle>
        <DialogContent>
          {historicoData && (
            <Box>
              {/* Informações gerais */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6">
                  {historicoData.monografia.titulo}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status atual: {getStatusDisplayName(historicoData.monografia.status)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Versão atual: {historicoData.monografia.versao_atual}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de resubmissões: {historicoData.monografia.numero_resubmissoes}
                </Typography>
              </Box>

              {/* Timeline de ações */}
              <Typography variant="h6" gutterBottom>📅 Timeline de Ações</Typography>
              {historicoData.timeline && historicoData.timeline.length > 0 ? (
                historicoData.timeline.map((item, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      border: 1, 
                      borderColor: 'grey.300', 
                      borderRadius: 1,
                      position: 'relative',
                      '&:before': index < historicoData.timeline.length - 1 ? {
                        content: '""',
                        position: 'absolute',
                        left: 16,
                        bottom: -16,
                        width: 2,
                        height: 16,
                        bgcolor: 'primary.main'
                      } : {}
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: 'primary.main' 
                          }} 
                        />
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {item.acao.replace(/_/g, ' ')}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.data).toLocaleString('pt-BR')}
                      </Typography>
                    </Box>
                    
                    <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1 }}>
                      Versão: {item.versao}
                    </Typography>
                    
                    {item.dados.observacoes && (
                      <Typography variant="body2" sx={{ 
                        mt: 1, 
                        p: 1, 
                        bgcolor: 'grey.100', 
                        borderRadius: 1,
                        fontStyle: 'italic' 
                      }}>
                        {item.dados.observacoes}
                      </Typography>
                    )}
                    
                    {item.dados.prazo && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                        ⏰ Prazo: {new Date(item.dados.prazo).toLocaleDateString('pt-BR')}
                      </Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Alert severity="info">Nenhuma ação registrada no histórico.</Alert>
              )}

              {/* Estatísticas */}
              {historicoData.estatisticas && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>📊 Estatísticas</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2">
                        <strong>Total de ações:</strong> {historicoData.estatisticas.total_acoes}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2">
                        <strong>Resubmissões:</strong> {historicoData.estatisticas.numero_resubmissoes}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2">
                        <strong>Tempo total:</strong> {
                          historicoData.estatisticas.tempo_total ? 
                          `${Math.ceil(historicoData.estatisticas.tempo_total / (1000 * 60 * 60 * 24))} dias` : 
                          'N/A'
                        }
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoricoDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÁLOGO DE VERSÕES DE DOCUMENTOS */}
      <Dialog
        open={versaoDialogOpen}
        onClose={() => setVersaoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          📄 Versões de Documentos
        </DialogTitle>
        <DialogContent>
          {versaoData && versaoData.length > 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Histórico de todas as versões submetidas desta monografia:
              </Typography>
              
              {versaoData.map((versao, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    mb: 2, 
                    p: 2, 
                    border: 1, 
                    borderColor: versao.acao === 'SUBMISSAO_INICIAL' ? 'success.main' : 'warning.main',
                    borderRadius: 1,
                    bgcolor: versao.acao === 'SUBMISSAO_INICIAL' ? 'success.50' : 'warning.50'
                  }}
                >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      {versao.acao === 'SUBMISSAO_INICIAL' ? '🎯 Submissão Inicial' : '🔄 Resubmissão'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(versao.data).toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="primary.main">
                    Versão: {versao.versao}
                  </Typography>
                  
                  {versao.numero_resubmissao > 0 && (
                    <Typography variant="caption" color="warning.main">
                      Resubmissão #{versao.numero_resubmissao}
                    </Typography>
                  )}
                  
                  {versao.documento && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                      📁 Arquivo: {versao.documento}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Alert severity="info">Nenhuma versão encontrada.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersaoDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sistema de Notificações */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MonografiaDetails;